import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { push } from 'redux-first-history';

import { authorize, register } from '@webapp/account/src/resources/profile/async';
import { profileSlice } from '@webapp/account/src/resources/profile/slice';
import { onAddQuestionOrRowFillers } from '@webapp/account/src/resources/questions/fillers';
import { getLinksStat, Actions as ReportsActions } from '@webapp/account/src/resources/reports';

import { CompletePageType, QuestionType } from '@webapp/common/resources/survey';

import { loadLogicData } from './logic';
import { addMail, sendCurrentMail } from './mailing/async';
import { Actions as PageActions, updatePageSettings } from './pages';
import { selectActivePageId, selectPageById } from './pages/selectors';
import { addQuestion, changeQuestionType, getAllQuestions, removeQuestion, saveQuestionParam } from './questions';
import { AccountRoutes } from './routes';
import { addSms, sendCurrentSms } from './smsing/async';
import type { States } from './states';
import { addonSave, addSurvey, getSurveyList, licenseApply, Actions as SurveyActions } from './survey';
import { AddonList, AddonListMain } from './survey/addons/addons';
import { withTests } from './survey/lib';
import { selectActiveSurvey, selectActiveSurveyId, selectMain } from './survey/selectors';

const listenerMiddleware = createListenerMiddleware<States>();

export enum ListOperation {
    ADD = 1,
    DEL = -1,
}

/**
 * Пересчитывает «количество вопросов для респондента» (QUESTION_COUNT) после
 * добавления/удаления вопроса на странице.
 *
 * Раньше значение всегда приравнивалось к общему числу вопросов, из-за чего
 * настроенная случайная выборка (например, 45 из 60) сбрасывалась на «все 60»
 * при любом редактировании списка вопросов. Сохраняем выбор, если он был
 * настроен, и увеличиваем его только когда на странице показывались все вопросы.
 */
const calcRandomCountAfterQuestionsChange = (
    prevQuestionCount: number,
    nextQuestionCount: number,
    randomCount: number,
): number => {
    const allQuestionsWereShown = !randomCount || randomCount >= prevQuestionCount;

    return allQuestionsWereShown ? nextQuestionCount : Math.min(randomCount, nextQuestionCount);
};

// TODO https://github.com/reduxjs/redux-toolkit/blob/master/examples/action-listener/counter/src/store.ts

listenerMiddleware.startListening({
    predicate: (action, currentState, previousState) => {
        const pageId = selectActivePageId(currentState);
        const prevPageId = selectActivePageId(previousState);
        return pageId && pageId !== prevPageId;
    },
    effect: async ({ payload }, { cancelActiveListeners, dispatch, getState }) => {
        const surveyId = payload?.surveyId || selectActiveSurveyId(getState());

        if (surveyId) {
            await dispatch(getAllQuestions({ surveyId })); // TODO this actually should be inside selectPage
        }

        cancelActiveListeners();

        await Promise.resolve();
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(removeQuestion.fulfilled),
    effect: async ({ payload: { qType, question, questionId } }, { dispatch, getState }) => {
        const state = getState();
        const survey = selectActiveSurvey(state);
        const { timerOn } = selectMain(state);
        const op = qType ? ListOperation.ADD : ListOperation.DEL;
        const type = qType || question?.type;
        const questionTypes = new Set(survey.questionTypes);
        op > 0 ? questionTypes.add(type) : questionTypes.delete(type);

        const nextWithTest = withTests(questionTypes);
        const prevWithTests = withTests(survey.questionTypes);
        const prevCompletePage = survey.addons.main.completePage;
        let nextCompletePage;

        if (nextWithTest && !prevWithTests) {
            nextCompletePage = CompletePageType.TEST_RESULTS;
        } else if (!nextWithTest && prevWithTests && prevCompletePage === CompletePageType.TEST_RESULTS) {
            nextCompletePage = CompletePageType.SUCCESS_TEXT;
        }

        dispatch(SurveyActions.setQuestionTypes({ questionTypes }));

        const mainPatch: Record<string, string | null> = {};

        if (op === ListOperation.DEL && timerOn === questionId) {
            mainPatch[AddonListMain.TIMER_ON] = null;
        }

        if (nextCompletePage) {
            mainPatch[AddonListMain.COMPLETE_PAGE] = nextCompletePage;
        }

        if (Object.keys(mainPatch).length > 0) {
            await dispatch(
                addonSave({
                    surveyId: survey.id,
                    key: null,
                    value: mainPatch,
                    name: AddonList.MAIN,
                }),
            );
        }

        return Promise.resolve();
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(addQuestion.fulfilled, removeQuestion.fulfilled),
    effect: async ({ payload, type }, { dispatch, getState }) => {
        const { pageId, questionType } = payload;
        const diff = type.startsWith('questions/add') ? ListOperation.ADD : ListOperation.DEL;
        const state = getState();
        const page = selectPageById(pageId)(state);
        const survey = selectActiveSurvey(state);
        const questionCount = page.questionCount + diff;
        const randomCount = calcRandomCountAfterQuestionsChange(page.questionCount, questionCount, page.randomCount);

        dispatch(PageActions.setQuestionCount({ pageId, questionCount }));
        if (type.startsWith('questions/add')) {
            const questionTypes = new Set(survey.questionTypes);
            questionTypes.add(questionType);
            dispatch(SurveyActions.setQuestionTypes({ questionTypes }));
        }

        if (randomCount !== page.randomCount) {
            await dispatch(
                updatePageSettings({
                    pageId: pageId,
                    params: { randomCount },
                }),
            );
        }

        return Promise.resolve();
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(addMail.fulfilled),
    effect: async ({
        payload: {
            mail: { id },
            surveyId,
        },
    }) => {
        AccountRoutes.emailStep1.go(surveyId, id);

        return Promise.resolve();
    },
});
listenerMiddleware.startListening({
    matcher: isAnyOf(addSms.fulfilled),
    effect: async ({
        payload: {
            sms: { id },
            surveyId,
        },
    }) => {
        AccountRoutes.smsStep1.go(surveyId, id);

        return Promise.resolve();
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(sendCurrentMail.fulfilled),
    effect: async ({ payload: { surveyId } }) => {
        AccountRoutes.shareEmail.go(surveyId);

        return Promise.resolve();
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(sendCurrentSms.fulfilled),
    effect: async ({ payload: { surveyId } }) => {
        AccountRoutes.shareSms.go(surveyId);

        return Promise.resolve();
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(loadLogicData.fulfilled),
    effect: async (_, { dispatch }) => {
        await dispatch(getSurveyList({ upsert: true }));

        return Promise.resolve();
    },
});

// TODO FIXME proper listener args
// @ts-ignore
listenerMiddleware.startListening({
    // @ts-ignore
    matcher: isAnyOf(
        ReportsActions.toggleLinkParamsFilter,
        ReportsActions.toggleExcludeQuestion,
        ReportsActions.includeReportResult,
        ReportsActions.excludeReportResult,
        ReportsActions.toggleExcludeQuestion,
        ReportsActions.toggleExcludedParam,
        ReportsActions.resetFilters,
        ReportsActions.removeParamFilter,
        ReportsActions.patchFilters,
    ),
    // @ts-ignore
    effect: async (_, { dispatch, getState }) => {
        const {
            Survey: { activeSurveyId: surveyId },
        } = getState();

        return dispatch(getLinksStat({ surveyId }));
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(authorize.fulfilled, register.fulfilled),
    effect: ({ payload: { url } }, { dispatch }) => {
        dispatch(push(url));
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(addSurvey.fulfilled, licenseApply.fulfilled),
    effect: (_, { dispatch }) => {
        dispatch(profileSlice.actions.licenseApply());
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(changeQuestionType.fulfilled),
    effect: (
        {
            meta: {
                arg: { questionId, type },
            },
        },
        { dispatch },
    ) => {
        if (type === QuestionType.MAX_DIFF) {
            dispatch(
                saveQuestionParam({ questionId, params: onAddQuestionOrRowFillers[QuestionType.MAX_DIFF].params }),
            );
        }
    },
});

export { listenerMiddleware };
